import { Button, Form, Input, InputNumber, message, Select } from 'antd';
import { FormComponentProps, FormItemProps } from 'antd/lib/form';
import * as React from 'react';
import { IMallMedicineEdit, IMallMedicineEquivalent, IMallMedicineSpecification, IMallPharmacyDetail, IMallTCMDetail, MedicineUnitLight } from 'src/entity/MallEntity';
import { ISpuMedicineDetail } from 'src/entity/SpuEntity';
import I18N from 'src/services/I18NService';
import * as uuid from 'uuid';
import { MallService } from '../../../services/MallService';
import MedicineSpecification from './MedicineSpecification';
import PriceChangeService, { EnumPriceType, IPriceChangeParam, IPriceChangeDetail } from 'src/services/PriceChangeService';
import { WordService } from '../../../services/WordService';
import MedicineEquivalent from './MedicineEquivalent';
import { EnumOrderType } from 'src/constants/Order';
import MedicineService from 'src/services/MedicineService';

/** 零售单位/中成药服用单位/批发单位 */
// const fullUnitList: string[] = ['克', '粒', '片', '丸', '袋', '支', '瓶', '盒'];

interface ITempMedicineForm extends Partial<IMallMedicineEdit> {
  specificationGroup: IMallMedicineSpecification;
  equivalentGroup: IMallMedicineEquivalent;
}

interface IMedicineEditProps extends FormComponentProps {
  isAgent: boolean;
  medicineInfo: IMallTCMDetail | undefined;
  /** 类目CODE */
  category: string;
  pharmacyList: IMallPharmacyDetail[];
  spuMedicineList: ISpuMedicineDetail[];
  cbEdit(): void;
}

interface IMedicineEditState {
  loadingBtn: boolean;
  hasPriceChangeApply: boolean;
  /** 零售单位 */
  retailUnit: string | undefined;
  /** 服用单位 */
  takeUnit: string | undefined;
  /** 规则编辑状态 */
  specificationDisabled: boolean;
  /** 批发单位 */
  wholesale_unit: string | undefined;
  /** 药库选择 */
  pharmacy: IMallPharmacyDetail | undefined;
  /** 零售单位/中成药服用单位/批发单位 */
  fullUnitList: MedicineUnitLight[]
}

class MedicineEdit extends React.Component<IMedicineEditProps, IMedicineEditState> {
  constructor(props: IMedicineEditProps) {
    super(props);
    this.state = {
      loadingBtn: false,
      hasPriceChangeApply: false,
      retailUnit: undefined,
      wholesale_unit: undefined,
      takeUnit: '克',
      specificationDisabled: !!props.medicineInfo,
      pharmacy: undefined,
      fullUnitList: []
    } as IMedicineEditState;
  }

  async componentDidMount() {
    const { medicineInfo, isAgent } = this.props;
    let pharmacy: IMallPharmacyDetail | undefined;
    if (!!medicineInfo) {
      if (!!medicineInfo.uuid && !isAgent) {
        const res = await PriceChangeService.hasChangePriceApply(medicineInfo.uuid);
        this.setState({
          hasPriceChangeApply: res,
        });
      }
      pharmacy = this.props.pharmacyList.find((i) => i.uuid === medicineInfo.medicine_group.uuid);
    }
    const fullUnitList = await MedicineService.fetchMedicineUnitDict('TCM');
    this.setState({
      pharmacy,
      fullUnitList
    });
  }

  componentWillUnmount() {
    this.setState = () => {
      return;
    };
  }

  handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    this.props.form.validateFields(async (err, values: ITempMedicineForm) => {
      if (!err) {
        if (this.state.loadingBtn) {
          return;
        }
        try {
          this.setState({
            loadingBtn: true,
          });
          let specification: number | undefined;
          let retailUnit: string | undefined;
          let wholesaleUnit: string | undefined;
          let takeUnit: string | undefined;
          let equivalent: number | undefined;

          if (!!values.specificationGroup) {
            specification = values.specificationGroup.specification;
            retailUnit = values.specificationGroup.retail_unit;
            takeUnit = values.specificationGroup.take_unit;
            wholesaleUnit = values.specificationGroup.wholesale_unit;
          }

          if (!!values.equivalentGroup) {
            equivalent = values.equivalentGroup.equivalent;
          }

          const { medicineInfo, isAgent } = this.props;
          if (!!medicineInfo) {
            // 编辑
            const data: Partial<IMallMedicineEdit> = {
              /** UUID */
              uuid: medicineInfo.uuid,
              /** 药品名称 */
              name: values.name,
              /** 药品简称 */
              short_name: values.short_name,
              /** 拼音 */
              pinyin: values.pinyin,
              /** 商家药品编码 */
              medicine_code: values.medicine_code,
              // /** 供应价 */
              // supply_price: values.supply_price,
              /** 生产商 */
              manufacturer: values.manufacturer,
              /** 产地 */
              production_place: values.production_place,
              /** 规格 */
              specification,
              /** 零售单位 */
              retail_unit: retailUnit,
              /** 批发单位 */
              wholesale_unit: wholesaleUnit,
              /** 服用单位 */
              take_unit: takeUnit,
              /** 起订量 */
              min_order_quantity: values.min_order_quantity,
              /** 订购单位 */
              order_unit: values.order_unit,
              /** 药效当量 */
              equivalent,
              /** 库存 */
              storage: values.storage,
              /** 新增入库 */
              add_storage: values.add_storage,
              /** 批号 */
              batch_number: values.batch_number,
              /** 优先级 TODO: @le  */
              priority: values.priority,
            };

            // 特殊处理 解决后端该字段必填 防止报错
            const pharmacy = this.props.pharmacyList.find((i) => i.uuid === values.medicine_group);
            if (pharmacy && pharmacy.order_type === EnumOrderType.处方订单) {
              data.min_order_quantity = 1;
              data.order_unit = 1;
              data.coefficient = 1;
            }

            // 特殊处理 针对药品订单需要使用form中的 批发单位及系数
            if (pharmacy && pharmacy.order_type === EnumOrderType.药品订单) {
              data.wholesale_unit = values.wholesale_unit;
              data.coefficient = values.coefficient;
            }

            if (!!data.uuid) {
              if (isAgent) {
                data.supply_price = values.supply_price;
                data.floor_price = values.floor_price;
              }
              const res = await MallService.updateMedicine(data);
              if (!!res) {
                if (
                  !isAgent &&
                  !this.state.hasPriceChangeApply &&
                  !!values.supply_price &&
                  values.supply_price > 0 &&
                  !!values.floor_price &&
                  values.floor_price > 0 &&
                  (values.supply_price !== medicineInfo.supply_price || values.floor_price !== medicineInfo.floor_price)
                ) {
                  const priceChangeDetail: IPriceChangeParam = {
                    sku: res.uuid,
                    details: [],
                  };

                  if (values.supply_price !== medicineInfo.supply_price) {
                    priceChangeDetail.details.push(this.changePriceDetail(res, values, EnumPriceType.SUPPLY_PRICE));
                  }

                  if (values.floor_price !== medicineInfo.floor_price) {
                    priceChangeDetail.details.push(this.changePriceDetail(res, values, EnumPriceType.FLOOR_PRICE));
                  }
                  const changeRes = await PriceChangeService.autoChangePrice(priceChangeDetail);
                  if (!!changeRes) {
                    message.success(`${I18N.get('common_submit_success')},${I18N.get('med_change_submit_success_tips')}`);
                  } else {
                    message.success(I18N.get('common_submit_success'));
                  }
                } else {
                  message.success(I18N.get('common_submit_success'));
                }
                this.setState({
                  loadingBtn: false,
                });
                this.props.cbEdit();
              }
            }
          } else {
            // 新增
            const data: Partial<IMallMedicineEdit> = {
              /** 药品名称 */
              name: values.name,
              /** 药品简称 */
              short_name: values.short_name,
              /** 脉景标准中药 */
              spu: values.spu,
              /** 药库 */
              medicine_group: values.medicine_group,
              /** 拼音 */
              pinyin: values.pinyin,
              // /** 供应价 */
              // supply_price: values.supply_price,
              /** 商家药品编码 */
              medicine_code: values.medicine_code,
              /** 生产商 */
              manufacturer: values.manufacturer,
              /** 产地 */
              production_place: values.production_place,
              /** 规格 */
              specification,
              /** 零售单位 */
              retail_unit: retailUnit,
              /** 批发单位 */
              wholesale_unit: wholesaleUnit,
              /** 服用单位 */
              take_unit: takeUnit,
              /** 起订量 */
              min_order_quantity: values.min_order_quantity,
              /** 订购单位 */
              order_unit: values.order_unit,
              /** 药效当量 */
              equivalent,
              /** 库存 */
              storage: values.storage,
              /** 新增入库 */
              add_storage: values.add_storage,
              /** 批号 */
              batch_number: values.batch_number,
              /** 优先级 TODO: @le  */
              priority: values.priority,
            };

            // 特殊处理 解决后端该字段必填 防止报错
            const pharmacy = this.props.pharmacyList.find((i) => i.uuid === values.medicine_group);
            if (pharmacy && pharmacy.order_type === EnumOrderType.处方订单) {
              data.min_order_quantity = 1;
              data.order_unit = 1;
              data.coefficient = 1;
            }
            // 特殊处理 针对药品订单需要使用form中的 批发单位及系数
            if (pharmacy && pharmacy.order_type === EnumOrderType.药品订单) {
              data.wholesale_unit = values.wholesale_unit;
              data.coefficient = values.coefficient;
            }

            if (!!data.spu && !!data.medicine_group) {
              if (isAgent) {
                data.supply_price = values.supply_price;
                data.floor_price = values.floor_price;
              }
              const res = await MallService.createMedicine(data);
              if (!!res) {
                if (
                  !isAgent &&
                  !!values.supply_price &&
                  values.supply_price > 0 &&
                  !!values.floor_price &&
                  values.floor_price > 0 &&
                  values.floor_price <= values.supply_price
                ) {
                  const priceChangeDetail: IPriceChangeParam = {
                    sku: res.uuid,
                    details: [this.changePriceDetail(res, values, EnumPriceType.SUPPLY_PRICE), this.changePriceDetail(res, values, EnumPriceType.FLOOR_PRICE)],
                  };
                  const changeRes = await PriceChangeService.autoChangePrice(priceChangeDetail);
                  if (!!changeRes) {
                    message.success(`${I18N.get('common_submit_success')},${I18N.get('med_change_submit_success_tips')}`);
                  }
                } else {
                  message.success(I18N.get('common_submit_success'));
                }
                this.setState({
                  loadingBtn: false,
                });
                this.props.cbEdit();
              }
            }
          }
        } catch (error) {
          this.setState({
            loadingBtn: false,
          });
          if (!!error.response) {
            message.error(JSON.stringify(error.response.data));
          } else {
            message.error(I18N.get('err_submit_failure'));
          }
        }
      }
    });
  };

  changePriceDetail = (originData: IMallMedicineEdit, newData: ITempMedicineForm, type: EnumPriceType): IPriceChangeDetail => {
    const valueKey = type === EnumPriceType.FLOOR_PRICE ? 'floor_price' : 'supply_price';
    return {
      price_type: type,
      original_price: originData[valueKey],
      new_price: newData[valueKey] || 0,
    };
  };

  checkSpecification = (rule: any, value: IMallTCMDetail, callback: any) => {
    let noHaveError = true;
    try {
      if (this.state.pharmacy) {
        if (this.state.pharmacy.order_type === EnumOrderType.处方订单) {
          if (value.specification > 0 && !!value.take_unit && !!value.retail_unit) {
            noHaveError = false;
          }
        } else if (this.state.pharmacy.order_type === EnumOrderType.药品订单) {
          if (value.specification > 0 && !!value.take_unit && !!value.wholesale_unit) {
            noHaveError = false;
          }
        }
      } else {
        noHaveError = false;
      }
      if (!noHaveError) {
        return callback();
      } else {
        callback(`${I18N.get('common_input')}${I18N.get('common_specification')}`);
      }
    } catch (err) {
      callback(err);
    }
  };

  checkEquivalent = (rule: any, value: IMallTCMDetail, callback: any) => {
    try {
      if (value.equivalent > 0) {
        return callback();
      } else {
        callback(`${I18N.get('common_input')}${I18N.get('med_equivalent')}`);
      }
    } catch (err) {
      callback(err);
    }
  };

  checkFloorPrice = (rule: any, value: number, callback: any) => {
    try {
      if (Number(value) <= (this.props.form.getFieldValue('supply_price') || 0)) {
        this.props.form.setFieldsValue({
          supply_price: this.props.form.getFieldValue('supply_price'),
        });
        return callback();
      }
      callback(`${I18N.get('med_buy_price')}${I18N.get('med_not_lower')}${I18N.get('med_preserve_price')}`);
    } catch (err) {
      callback(err);
    }
  };

  checkSupplyPrice = (rule: any, value: number, callback: any) => {
    try {
      if (Number(value) >= (this.props.form.getFieldValue('floor_price') || 0)) {
        this.props.form.setFieldsValue({
          floor_price: this.props.form.getFieldValue('floor_price'),
        });
        return callback();
      }
      callback(`${I18N.get('med_buy_price')}${I18N.get('med_not_lower')}${I18N.get('med_preserve_price')}`);
    } catch (err) {
      callback(err);
    }
  };

  filterSpuOption = (inputValue: string, option: React.ReactElement<any>) => {
    return (option.props.children || '').indexOf(inputValue || '') > -1;
  };

  onChangePharmacy = async (pharmacyUUID: string | undefined) => {
    if (!!pharmacyUUID) {
      const res = await MallService.searchMedicineDetail({
        medicine_group: pharmacyUUID,
        is_deleted: false,
        limit: 1,
        offset: 0,
      });
      const pharmacy = this.props.pharmacyList.find((i) => i.uuid === pharmacyUUID);
      if (!!res && (res.results || []).length > 0) {
        const exitMedicineInfo = res.results[0];
        if (!!exitMedicineInfo && !!exitMedicineInfo.uuid) {
          this.setState({
            retailUnit: exitMedicineInfo.retail_unit,
            takeUnit: exitMedicineInfo.take_unit,
            specificationDisabled: true,
            wholesale_unit: exitMedicineInfo.wholesale_unit,
            pharmacy,
          });
        }
      } else {
        this.setState({
          retailUnit: undefined,
          takeUnit: '克',
          specificationDisabled: false,
          wholesale_unit: undefined,
          pharmacy,
        });
      }
    }
  };

  /**
   * 药品简称失去焦点触发
   *
   * @memberof MedicineEdit
   */
  shortNameOnBlur = async () => {
    const shortName = this.props.form.getFieldValue('short_name');
    const pinyin = this.props.form.getFieldValue('pinyin');
    if (!!shortName) {
      if (!pinyin) {
        const res = await WordService.getPinyinByDesc(shortName);
        if (!!res) {
          this.props.form.setFieldsValue({
            pinyin: res,
          });
        }
      }
    } else {
      this.props.form.setFieldsValue({
        pinyin: undefined,
      });
    }
  };

  specificationGroupChange = (e: any) => {
    const equivalentGroup = this.props.form.getFieldValue('equivalentGroup');
    this.props.form.setFieldsValue({ equivalentGroup: { ...equivalentGroup, retail_unit: e.retail_unit } });
  };

  equivalentGroupChange = (e: any) => {
    const specificationGroup = this.props.form.getFieldValue('specificationGroup');
    this.props.form.setFieldsValue({ specificationGroup: { ...specificationGroup, retail_unit: e.retail_unit } });
  };

  render() {
    const { pharmacyList, spuMedicineList, medicineInfo, category } = this.props;
    const { getFieldDecorator } = this.props.form;
    const formItemLayout: FormItemProps = {
      labelCol: { span: 6 },
      wrapperCol: { span: 15 },
    };

    let takeUnit = '';
    const tempSpecificationGroup: IMallMedicineSpecification = this.props.form.getFieldValue('specificationGroup');
    if (!!tempSpecificationGroup && !!tempSpecificationGroup.take_unit) {
      takeUnit = `(${tempSpecificationGroup.take_unit})`;
    }

    let medicineSpuUUID: string | undefined;
    if (!!medicineInfo) {
      medicineSpuUUID = medicineInfo.spu.uuid;
    }

    const medicine_group = this.props.form.getFieldValue('medicine_group');
    const pharmacy = this.props.pharmacyList.find((i) => i.uuid === medicine_group);
    return (
      <div>
        <Form className="medicine-edit-form" onSubmit={this.handleSubmit}>
          {/* 药品ID --编辑时显示 */}
          {!!medicineInfo && (
            <Form.Item {...formItemLayout} label={`${I18N.get('med_agent_code')}`}>
              {getFieldDecorator(`code`, {
                initialValue: medicineInfo.code,
              })(<Input disabled={true} />)}
            </Form.Item>
          )}
          {/* 脉景品名 */}
          <Form.Item label={I18N.get('med_spu_name')} {...formItemLayout}>
            {getFieldDecorator(`spu`, {
              rules: [
                {
                  required: true,
                  message: `${I18N.get('common_choose')}${I18N.get('med_spu_name')}`,
                },
              ],
              initialValue: medicineSpuUUID || null,
            })(
              <Select
                showSearch={true}
                disabled={!!medicineInfo}
                placeholder={`${I18N.get('common_choose')}${I18N.get('med_spu_name')}`}
                filterOption={this.filterSpuOption}
                defaultActiveFirstOption={false}
              >
                {spuMedicineList
                  .filter((t) => t.uuid === medicineSpuUUID || !t.is_deleted)
                  .map((f) => {
                    return (
                      <Select.Option key={`${f.uuid}_${f.name}`} value={f.uuid}>
                        {!!f.is_deleted ? `[${I18N.get('common_delete')}]${f.name}` : f.name}
                      </Select.Option>
                    );
                  })}
              </Select>
            )}
          </Form.Item>
          {/* 药库 */}
          <Form.Item label={I18N.get('common_pharmacy')} {...formItemLayout}>
            {getFieldDecorator(`medicine_group`, {
              rules: [
                {
                  required: true,
                  message: `${I18N.get('common_choose')}${I18N.get('common_pharmacy')}`,
                },
              ],
              initialValue: !!medicineInfo ? medicineInfo.medicine_group.uuid : null,
            })(
              <Select disabled={!!medicineInfo} onChange={this.onChangePharmacy} placeholder={`${I18N.get('common_choose')}${I18N.get('med_spu_name')}`}>
                {pharmacyList
                  .filter((t) => t.category_code === category && !t.is_deleted)
                  .map((f) => {
                    return (
                      <Select.Option key={`${f.uuid}_${f.name}`} value={f.uuid}>
                        {f.is_active ? f.name : `${f.name}(${I18N.get('common_status_disable')})`}
                      </Select.Option>
                    );
                  })}
              </Select>
            )}
          </Form.Item>
          {/* 药品名称 */}
          <Form.Item label={I18N.get('med_agent_name')} {...formItemLayout}>
            {getFieldDecorator(`name`, {
              rules: [
                {
                  required: true,
                  message: `${I18N.get('common_input')}${I18N.get('med_agent_name')}`,
                },
              ],
              initialValue: !!medicineInfo ? medicineInfo.name : undefined,
            })(<Input name="medicine-edit-name" placeholder={`${I18N.get('common_input')}${I18N.get('med_agent_name')}`} />)}
          </Form.Item>
          {/* 药品简称 */}
          <Form.Item label={I18N.get('med_short_name')} {...formItemLayout}>
            {getFieldDecorator(`short_name`, {
              rules: [
                {
                  required: true,
                  message: `${I18N.get('common_input')}${I18N.get('med_short_name')}`,
                },
              ],
              initialValue: !!medicineInfo ? medicineInfo.short_name : undefined,
            })(
              <Input name="medicine-edit-short-name" onBlur={this.shortNameOnBlur} placeholder={`${I18N.get('common_input')}${I18N.get('med_short_name')}`} />
            )}
          </Form.Item>
          {/* 简称拼音 */}
          <Form.Item label={I18N.get('med_short_name_pinyin2')} {...formItemLayout}>
            {getFieldDecorator(`pinyin`, {
              rules: [
                {
                  required: true,
                  message: `${I18N.get('common_input')}${I18N.get('med_short_name_pinyin2')}`,
                },
              ],
              initialValue: !!medicineInfo ? medicineInfo.pinyin : null,
            })(<Input placeholder={`${I18N.get('common_input')}${I18N.get('med_short_name_pinyin2')}`} />)}
          </Form.Item>
          {/* 商家药品编码 */}
          <Form.Item label={I18N.get('med_medicine_code')} {...formItemLayout}>
            {getFieldDecorator(`medicine_code`, {
              rules: [
                {
                  required: true,
                  message: `${I18N.get('common_input')}${I18N.get('med_medicine_code')}`,
                },
              ],
              initialValue: !!medicineInfo ? medicineInfo.medicine_code : null,
            })(<Input placeholder={`${I18N.get('common_input')}${I18N.get('med_medicine_code')}`} />)}
          </Form.Item>
          {/* 规格/服用单位/零售单位/批发单位 */}
          {(
            <Form.Item label={I18N.get('common_specification')} {...formItemLayout}>
              {getFieldDecorator(`specificationGroup`, {
                rules: [{ required: true }, { validator: this.checkSpecification }],
                initialValue: {
                  specification: !!medicineInfo ? medicineInfo.specification : undefined,
                  retail_unit: !!medicineInfo ? medicineInfo.retail_unit : this.state.retailUnit,
                  take_unit: !!medicineInfo ? medicineInfo.take_unit : this.state.takeUnit,
                  wholesale_unit: !!medicineInfo ? medicineInfo.wholesale_unit : this.state.wholesale_unit,
                },
              })(
                <MedicineSpecification
                  key={uuid.v4()}
                  disabled={false}
                  pharmacy={this.state.pharmacy}
                  onChange={this.specificationGroupChange}
                  fullUnitList={this.state.fullUnitList}
                />
              )}
            </Form.Item>
          )}
          {/* 药品订购 显示批发单位 */}
          {((pharmacy && pharmacy.order_type === EnumOrderType.药品订单) || !pharmacy) && (
            <Form.Item label={I18N.get('med_wholesale_unit')} {...formItemLayout}>
              {getFieldDecorator(`wholesale_unit`, {
                rules: [
                  {
                    required: true,
                    message: `${I18N.get('common_choose')}${I18N.get('med_wholesale_unit')}`,
                  },
                ],
                initialValue: !!medicineInfo ? medicineInfo.wholesale_unit : this.state.wholesale_unit,
              })(
                // <Select disabled={this.state.specificationDisabled} placeholder={`${I18N.get('common_choose')}${I18N.get('med_wholesale_unit')}`}>
                <Select placeholder={`${I18N.get('common_choose')}${I18N.get('med_wholesale_unit')}`}>
                  {this.state.fullUnitList.map((unit) => {
                    return (
                      <Select.Option key={`${unit.id}`} value={unit.name}>
                        {unit.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              )}
            </Form.Item>
          )}
          {/* 药品订购 系数 */}
          {pharmacy && pharmacy.order_type === EnumOrderType.药品订单 && (
            <Form.Item label={I18N.get('med_coefficient')} {...formItemLayout}>
              {getFieldDecorator(`coefficient`, {
                rules: [
                  {
                    required: true,
                    message: '请输入用于小包装零售时的价格转换系数',
                  },
                ],
                initialValue: !!medicineInfo ? medicineInfo.coefficient : null,
              })(<InputNumber min={0.01} precision={2} max={10000} style={{ width: '100%' }} placeholder={`请输入用于小包装零售时的价格转换系数`} />)}
            </Form.Item>
          )}
          {/* 药效当量/规格/零售单位 --中药 */}
          {(
            <Form.Item label={I18N.get('med_equivalent')} {...formItemLayout}>
              {getFieldDecorator(`equivalentGroup`, {
                rules: [{ required: true }, { validator: this.checkEquivalent }],
                initialValue: {
                  equivalent: !!medicineInfo ? (medicineInfo as IMallTCMDetail).equivalent : null,
                  retail_unit: !!medicineInfo ? medicineInfo.retail_unit : this.state.retailUnit,
                },
              })(<MedicineEquivalent key={uuid.v4()} disabled={false} category={category}
                fullUnitList={this.state.fullUnitList} onChange={this.equivalentGroupChange} />)}
            </Form.Item>
          )}
          {/* 起订量 */}
          {pharmacy && pharmacy.order_type === EnumOrderType.药品订单 && (
            <Form.Item label={I18N.get('order_min_purchase_amount')} {...formItemLayout}>
              {getFieldDecorator(`min_order_quantity`, {
                rules: [
                  {
                    required: true,
                    message: `${I18N.get('common_input')}${I18N.get('order_min_purchase_amount')}`,
                  },
                ],
                initialValue: !!medicineInfo ? medicineInfo.min_order_quantity : null,
              })(
                <InputNumber
                  min={1}
                  precision={0}
                  style={{ width: '100%' }}
                  placeholder={`${I18N.get('common_input')}${I18N.get('order_min_purchase_amount')}`}
                />
              )}
            </Form.Item>
          )}
          {/* 订购单位 */}
          {pharmacy && pharmacy.order_type === EnumOrderType.药品订单 && (
            <Form.Item label={I18N.get('order_purchase_unit')} {...formItemLayout}>
              {getFieldDecorator(`order_unit`, {
                rules: [
                  {
                    required: true,
                    message: `${I18N.get('common_input')}${I18N.get('order_purchase_unit')}`,
                  },
                ],
                initialValue: !!medicineInfo ? medicineInfo.order_unit : null,
              })(<InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder={`${I18N.get('common_input')}${I18N.get('order_purchase_unit')}`} />)}
            </Form.Item>
          )}
          {/* 供货价 */}
          <Form.Item label={`${I18N.get('med_buy_price')}(￥)`} {...formItemLayout}>
            {getFieldDecorator(`supply_price`, {
              rules: [
                {
                  required: !this.state.hasPriceChangeApply,
                  message: `${I18N.get('common_input')}${I18N.get('med_buy_price')}`,
                },
                { validator: this.checkSupplyPrice },
              ],
              initialValue: !!medicineInfo ? medicineInfo.supply_price : null,
            })(
              <InputNumber
                min={0.0001}
                precision={4}
                style={{ width: '100%' }}
                disabled={this.state.hasPriceChangeApply}
                placeholder={`${I18N.get('common_input')}${I18N.get('med_buy_price')}`}
              />
            )}
          </Form.Item>
          {/* 底价 */}
          <Form.Item label={`${I18N.get('med_preserve_price')}(￥)`} {...formItemLayout}>
            {getFieldDecorator(`floor_price`, {
              rules: [
                {
                  required: !this.state.hasPriceChangeApply,
                  message: `${I18N.get('common_input')}${I18N.get('med_preserve_price')}`,
                },
                { validator: this.checkFloorPrice },
              ],
              initialValue: !!medicineInfo ? medicineInfo.floor_price : null,
            })(
              <InputNumber
                min={0.0001}
                precision={4}
                style={{ width: '100%' }}
                disabled={this.state.hasPriceChangeApply}
                placeholder={`${I18N.get('common_input')}${I18N.get('med_preserve_price')}`}
              />
            )}
          </Form.Item>
          {/* 生产商 */}
          <Form.Item label={I18N.get('med_manufacturer')} {...formItemLayout}>
            {getFieldDecorator(`manufacturer`, {
              rules: [
                {
                  required: false,
                  message: `${I18N.get('common_input')}${I18N.get('med_manufacturer')}`,
                },
              ],
              initialValue: !!medicineInfo ? medicineInfo.manufacturer : null,
            })(<Input placeholder={`${I18N.get('common_input')}${I18N.get('med_manufacturer')}`} />)}
          </Form.Item>
          {/* 产地 */}
          <Form.Item label={I18N.get('med_origin_place')} {...formItemLayout}>
            {getFieldDecorator(`production_place`, {
              rules: [
                {
                  required: false,
                  message: `${I18N.get('common_input')}${I18N.get('med_origin_place')}`,
                },
              ],
              initialValue: !!medicineInfo ? medicineInfo.production_place : null,
            })(<Input placeholder={`${I18N.get('common_input')}${I18N.get('med_origin_place')}`} />)}
          </Form.Item>

          {/* 批号 */}
          <Form.Item label={I18N.get('med_batch_number')} {...formItemLayout}>
            {getFieldDecorator(`batch_number`, {
              rules: [
                {
                  required: false,
                  message: `${I18N.get('common_input')}${I18N.get('med_batch_number')}`,
                },
              ],
              initialValue: !!medicineInfo ? medicineInfo.batch_number : null,
            })(<Input placeholder={`${I18N.get('common_input')}${I18N.get('med_batch_number')}`} />)}
          </Form.Item>

          {medicineInfo && (
            <section
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginLeft: '139px',
                marginRight: '90px',
                marginBottom: '14px',
              }}
            >
              {/* 库存 */}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <label style={{ width: '40px' }}> {I18N.get('common_storage')} :</label>
                <Input
                  style={{ flex: 1 }}
                  disabled={true}
                  value={medicineInfo.storage}
                  placeholder={`${I18N.get('common_input')}${I18N.get('common_storage')}`}
                />
              </div>

              {/* 饮片剂量库存 ( 库存 * 当量 ) */}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <label style={{ width: '100px' }}>{I18N.get('common_pieces_stock')} :</label>
                <Input
                  style={{ flex: 1 }}
                  disabled={true}
                  value={(medicineInfo.storage || 0) * (medicineInfo as IMallTCMDetail).equivalent}
                  placeholder={`${I18N.get('common_input')}${I18N.get('common_storage')}`}
                />
              </div>
            </section>
          )}

          {/* 新增入库 */}
          <Form.Item label={I18N.get('common_add_storage')} {...formItemLayout}>
            {getFieldDecorator(`add_storage`, {
              initialValue: !!medicineInfo ? medicineInfo.add_storage : null,
            })(<InputNumber precision={0} max={10000000000000000} style={{ width: '100%' }} placeholder={`${I18N.get('common_input')}${I18N.get('common_storage')}`} />)}
          </Form.Item>
          <Form.Item label={'优先级'} {...formItemLayout}>
            {getFieldDecorator(`priority`, {
              rules: [
                {
                  required: true,
                  message: '请输入优先级',
                },
              ],
              initialValue: !!medicineInfo ? medicineInfo.priority : null,
            })(<InputNumber precision={4} min={0} max={999999} style={{ width: '100%' }} placeholder={`优先级较小的药品会在门诊导入处方时优先选用`} />)}
          </Form.Item>
          <Form.Item wrapperCol={{ span: 12, offset: 6 }}>
            <Button type="primary" htmlType="submit">
              {I18N.get('common_save')}
            </Button>
          </Form.Item>
        </Form>
      </div>
    );
  }
}

const MedicineEditForm = Form.create<IMedicineEditProps>()(MedicineEdit);
export default MedicineEditForm;



// WEBPACK FOOTER //
// ./src/components/views/Mall/MedicineEditForm.tsx